/**
 * Cities Redux Slice
 * State management for cities domain
 */

import { createSlice, type PayloadAction } from '@spcc/core'

import type {
  City,
  CityListItem,
  CityStats,
  CitiesFilter,
  PaginationMeta,
  CreateCityRequest,
  UpdateCityRequest,
  RegionSummary,
  CountrySummary,
} from '../domain/types'
import { DEFAULT_CITY_FILTER, DEFAULT_CITY_STATS } from '../domain/constants'

// ===========================================
// State Interface
// ===========================================

export interface CitiesState {
  items: CityListItem[]
  selectedCity: City | null
  stats: CityStats
  filter: CitiesFilter
  pagination: PaginationMeta
  // Reference data
  availableRegions: RegionSummary[]
  availableCountries: CountrySummary[]
  // UI state
  loading: boolean
  detailLoading: boolean
  error: string | null
}

// ===========================================
// Initial State
// ===========================================

const initialState: CitiesState = {
  items: [],
  selectedCity: null,
  stats: DEFAULT_CITY_STATS,
  filter: DEFAULT_CITY_FILTER,
  pagination: {
    page: 1,
    pageSize: 20,
    pageCount: 1,
    total: 0,
  },
  availableRegions: [],
  availableCountries: [],
  loading: false,
  detailLoading: false,
  error: null,
}

// ===========================================
// Slice
// ===========================================

export const citiesSlice = createSlice({
  name: 'cities',
  initialState,
  reducers: {
    // ==========================================
    // Data Setters
    // ==========================================

    setCities: (state, action: PayloadAction<CityListItem[]>) => {
      state.items = action.payload
      state.loading = false
      state.error = null
    },

    setPagination: (state, action: PayloadAction<PaginationMeta>) => {
      state.pagination = action.payload
    },

    setCityStats: (state, action: PayloadAction<CityStats>) => {
      state.stats = action.payload
    },

    setSelectedCity: (state, action: PayloadAction<City | null>) => {
      state.selectedCity = action.payload
      state.detailLoading = false
    },

    setFilter: (state, action: PayloadAction<Partial<CitiesFilter>>) => {
      const nonPageKeys = ['search', 'isActive', 'regionId', 'countryId']
      const isNonPageFilterChange = nonPageKeys.some(key => key in action.payload)
      state.filter = {
        ...state.filter,
        ...action.payload,
        // Reset to page 1 whenever a non-pagination filter changes
        ...(isNonPageFilterChange && !('page' in action.payload) ? { page: 1 } : {}),
      }
    },

    setAvailableRegions: (state, action: PayloadAction<RegionSummary[]>) => {
      state.availableRegions = action.payload
    },

    setAvailableCountries: (state, action: PayloadAction<CountrySummary[]>) => {
      state.availableCountries = action.payload
    },

    // ==========================================
    // Fetch Actions (saga triggers)
    // ==========================================

    fetchCities: (state, _action: PayloadAction<CitiesFilter | undefined>) => {
      state.loading = true
      state.error = null
    },

    fetchCitiesSuccess: (
      state,
      action: PayloadAction<{ data: CityListItem[]; pagination: PaginationMeta }>
    ) => {
      state.items = action.payload.data
      state.pagination = action.payload.pagination
      state.loading = false
    },

    fetchCitiesFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    fetchCityDetail: (state, _action: PayloadAction<string>) => {
      state.detailLoading = true
      state.error = null
    },

    fetchCityDetailSuccess: (state, action: PayloadAction<City>) => {
      state.selectedCity = action.payload
      state.detailLoading = false
    },

    fetchCityDetailFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.detailLoading = false
    },

    fetchCityStats: (state) => {
      // No loading state change for stats
    },

    fetchCityStatsSuccess: (state, action: PayloadAction<CityStats>) => {
      state.stats = action.payload
    },

    fetchAvailableRegions: (state) => {
      // Triggered to fetch regions for form
    },

    fetchAvailableRegionsSuccess: (state, action: PayloadAction<RegionSummary[]>) => {
      state.availableRegions = action.payload
    },

    fetchAvailableCountries: (state) => {
      // Triggered to fetch countries for form
    },

    fetchAvailableCountriesSuccess: (state, action: PayloadAction<CountrySummary[]>) => {
      state.availableCountries = action.payload
    },

    // ==========================================
    // CRUD Actions (saga triggers)
    // ==========================================

    createCity: (state, _action: PayloadAction<CreateCityRequest>) => {
      state.loading = true
      state.error = null
    },

    createCitySuccess: (state, action: PayloadAction<City>) => {
      // Prepend to items list as list item
      const newItem: CityListItem = {
        id: action.payload.id,
        documentId: action.payload.documentId,
        code: action.payload.code,
        name: action.payload.name,
        stateProvince: action.payload.stateProvince,
        isActive: action.payload.isActive,
        regionId: action.payload.region?.id,
        regionCode: action.payload.region?.code,
        regionName: action.payload.region?.name,
        countryId: action.payload.country?.id,
        countryCode: action.payload.country?.code,
        countryName: action.payload.country?.name,
        createdAt: action.payload.createdAt,
        updatedAt: action.payload.updatedAt,
      }
      state.items.unshift(newItem)
      state.loading = false
    },

    createCityFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    updateCity: (
      state,
      _action: PayloadAction<{ documentId: string; data: UpdateCityRequest }>
    ) => {
      state.loading = true
      state.error = null
    },

    updateCitySuccess: (state, action: PayloadAction<City>) => {
      const index = state.items.findIndex(
        (item) => item.documentId === action.payload.documentId
      )
      if (index !== -1) {
        state.items[index] = {
          ...state.items[index],
          name: action.payload.name,
          stateProvince: action.payload.stateProvince,
          isActive: action.payload.isActive,
          regionId: action.payload.region?.id,
          regionCode: action.payload.region?.code,
          regionName: action.payload.region?.name,
          countryId: action.payload.country?.id,
          countryCode: action.payload.country?.code,
          countryName: action.payload.country?.name,
          updatedAt: action.payload.updatedAt,
        }
      }
      if (state.selectedCity?.documentId === action.payload.documentId) {
        state.selectedCity = action.payload
      }
      state.loading = false
    },

    updateCityFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    deleteCity: (state, _action: PayloadAction<string>) => {
      state.loading = true
      state.error = null
    },

    deleteCitySuccess: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.documentId !== action.payload)
      if (state.selectedCity?.documentId === action.payload) {
        state.selectedCity = null
      }
      state.loading = false
    },

    deleteCityFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // ==========================================
    // Error Handling
    // ==========================================

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    clearError: (state) => {
      state.error = null
    },

    // ==========================================
    // Reset
    // ==========================================

    reset: () => initialState,

    clearSelectedCity: (state) => {
      state.selectedCity = null
    },
  },
})

// ===========================================
// Exports
// ===========================================

export const citiesActions = citiesSlice.actions
export const citiesReducer = citiesSlice.reducer
